import { Button } from "../components/ui/button";
import {
  MessageSquare,
  Users,
  Shield,
  Zap,
  Globe,
  Lock,
  ArrowRight,
  Sparkles,
  Image,
  Video,
  CreditCard,
  QrCode,
  Star,
  Search,
  Code,
  Headphones,
  ChevronDown,
  ExternalLink,
  CheckCircle2,
  ShieldCheck,
  Fingerprint,
} from "lucide-react";
import { useRef } from "react";

const SIGNUP_URL = "https://app.okgo.mx/signup?q=signup&type=Cliente&step=1";

const openSignup = () => {
  window.open(SIGNUP_URL, "_blank", "noopener,noreferrer");
};

// Feature card component
const FeatureCard = ({ icon: Icon, title, description, color }) => (
  <div className="group relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-red-500/50 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-red-500/10">
    <div className={`w-14 h-14 rounded-xl ${color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
      <Icon className="w-7 h-7 text-white" />
    </div>
    <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
    <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
  </div>
);

// Stat card component
const StatCard = ({ value, label }) => (
  <div className="text-center">
    <div className="text-4xl md:text-5xl font-bold text-white mb-2">{value}</div>
    <div className="text-slate-400 text-sm">{label}</div>
  </div>
);

const PoderesLanding = () => {
  const featuresRef = useRef(null);

  const scrollToFeatures = () => {
    featuresRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const features = [
    {
      icon: Lock,
      title: "Cifrado Extremo a Extremo",
      description: "Tus mensajes están protegidos con cifrado E2E. Solo tú y tu destinatario pueden leerlos.",
      color: "bg-gradient-to-br from-emerald-500 to-emerald-600"
    },
    {
      icon: MessageSquare,
      title: "Chat en Tiempo Real",
      description: "Mensajería instantánea con WebSockets. Recibe y envía mensajes al instante.",
      color: "bg-gradient-to-br from-blue-500 to-blue-600"
    },
    {
      icon: Image,
      title: "Comparte Multimedia",
      description: "Envía fotos, videos y archivos de hasta 50MB. Vista previa integrada.",
      color: "bg-gradient-to-br from-purple-500 to-purple-600"
    },
    {
      icon: Video,
      title: "YouTube Embebido",
      description: "Comparte videos de YouTube y míralos directamente en el chat sin salir.",
      color: "bg-gradient-to-br from-red-500 to-red-600"
    },
    {
      icon: CreditCard,
      title: "Pagos con Stripe",
      description: "Envía y recibe pagos de forma segura entre usuarios con integración Stripe.",
      color: "bg-gradient-to-br from-indigo-500 to-indigo-600"
    },
    {
      icon: QrCode,
      title: "Perfil Compartible",
      description: "Comparte tu perfil con un enlace único o código QR personalizado.",
      color: "bg-gradient-to-br from-cyan-500 to-cyan-600"
    },
    {
      icon: Search,
      title: "Búsqueda Avanzada",
      description: "Busca mensajes, usuarios y conversaciones. Encuentra cualquier cosa al instante.",
      color: "bg-gradient-to-br from-amber-500 to-amber-600"
    },
    {
      icon: Star,
      title: "Usuarios Favoritos",
      description: "Marca usuarios como favoritos para acceder rápidamente a tus contactos importantes.",
      color: "bg-gradient-to-br from-yellow-500 to-yellow-600"
    },
    {
      icon: Shield,
      title: "Bloqueo de Usuarios",
      description: "Control total sobre quién puede contactarte. Bloquea usuarios no deseados.",
      color: "bg-gradient-to-br from-slate-500 to-slate-600"
    },
    {
      icon: Globe,
      title: "20 Idiomas",
      description: "Interfaz disponible en 20 idiomas diferentes para usuarios de todo el mundo.",
      color: "bg-gradient-to-br from-teal-500 to-teal-600"
    },
    {
      icon: Sparkles,
      title: "GIFs con Giphy",
      description: "Expresa tus emociones con miles de GIFs animados de la biblioteca de Giphy.",
      color: "bg-gradient-to-br from-pink-500 to-pink-600"
    },
    {
      icon: Code,
      title: "Widget Embebible",
      description: "Integra OkChat en tu sitio web con un simple código. Iframe o widget flotante.",
      color: "bg-gradient-to-br from-orange-500 to-orange-600"
    },
    {
      icon: Headphones,
      title: "Modo Soporte",
      description: "Conecta visitantes de tu web directamente con tu equipo de soporte.",
      color: "bg-gradient-to-br from-violet-500 to-violet-600"
    },
    {
      icon: Fingerprint,
      title: "PIN de Seguridad",
      description: "Protege tu cuenta con un PIN de 4 dígitos. Seguridad adicional para tu privacidad.",
      color: "bg-gradient-to-br from-rose-500 to-rose-600"
    },
    {
      icon: Users,
      title: "Redes Sociales",
      description: "Conecta tus perfiles de Facebook, Instagram, TikTok, X, LinkedIn y Pinterest.",
      color: "bg-gradient-to-br from-sky-500 to-sky-600"
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white overflow-x-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-red-500/10 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-red-600/10 rounded-full blur-[100px] animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-red-500/5 rounded-full blur-[150px]" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-slate-800/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg shadow-red-500/30">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                OkChat
              </span>
            </div>

            {/* Nav Links */}
            <nav className="hidden md:flex items-center gap-8">
              <button onClick={scrollToFeatures} className="text-slate-400 hover:text-white transition-colors text-sm">
                Funciones
              </button>
            </nav>

            {/* CTA */}
            <Button
              onClick={openSignup}
              variant="ghost"
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              data-testid="header-comenzar-btn"
            >
              Comenzar <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 pt-20 pb-32 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div className="text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-full mb-8">
                <ShieldCheck className="w-4 h-4 text-red-400" />
                <span className="text-red-300 text-sm font-medium">Cifrado E2E • 100% Seguro</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Mensajería{" "}
                <span className="bg-gradient-to-r from-red-400 via-red-500 to-red-600 bg-clip-text text-transparent">
                  Empresarial
                </span>
                <br />
                <span className="text-slate-300">Segura y Profesional</span>
              </h1>

              <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto lg:mx-0">
                Chat en tiempo real con cifrado extremo, pagos integrados y todas las herramientas que tu negocio necesita.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 mb-10">
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span>Cifrado E2E</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span>Verificado Trustindex</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span>+1000 usuarios</span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 py-8 border-t border-slate-800">
                <StatCard value="99%" label="Uptime" />
                <StatCard value="4.6★" label="Rating" />
                <StatCard value="24/7" label="Soporte" />
              </div>
            </div>

            {/* Right - Signup CTA Card */}
            <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
              <div className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50 shadow-2xl shadow-red-500/5">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-500/30">
                    <MessageSquare className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">Accede a OkChat</h2>
                  <p className="text-slate-400 text-sm">Inicia sesión o crea una cuenta nueva</p>
                </div>

                <div className="space-y-4">
                  <Button
                    onClick={openSignup}
                    className="w-full h-12 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-medium transition-all duration-200"
                    data-testid="hero-ingresar-btn"
                  >
                    Ingresar <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button
                    onClick={openSignup}
                    variant="outline"
                    className="w-full h-12 border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200 rounded-xl font-medium transition-all duration-200"
                    data-testid="hero-crear-cuenta-btn"
                  >
                    Crear cuenta gratis <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>

                {/* Security Note */}
                <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
                  <Lock className="w-3 h-3" />
                  <span>Conexión segura con cifrado E2E</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <button onClick={scrollToFeatures} className="text-slate-500 hover:text-white transition-colors">
            <ChevronDown className="w-8 h-8" />
          </button>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="relative z-10 py-24 px-4 bg-gradient-to-b from-transparent via-slate-900/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-full mb-6">
              <Zap className="w-4 h-4 text-red-400" />
              <span className="text-red-300 text-sm font-medium">Todas las funciones</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Todo lo que necesitas en{" "}
              <span className="bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">
                un solo lugar
              </span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              OkChat integra mensajería, pagos, multimedia y herramientas empresariales
              en una plataforma segura y fácil de usar.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <FeatureCard key={index} {...feature} />
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="relative z-10 py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-slate-700/50">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  <span className="text-emerald-300 text-sm font-medium">Máxima Seguridad</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Cifrado Extremo a Extremo
                </h2>
                <p className="text-slate-400 mb-6">
                  Cada mensaje que envías está protegido con cifrado AES-256 de grado militar.
                  Ni siquiera nosotros podemos leer tus conversaciones. Tu privacidad es nuestra prioridad.
                </p>
                <ul className="space-y-3">
                  {[
                    "Cifrado AES-256 para todos los mensajes",
                    "PIN de 4 dígitos para acceso seguro",
                    "Datos almacenados de forma encriptada",
                    "Control total sobre tu privacidad"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex justify-center">
                <div className="relative">
                  <div className="w-48 h-48 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 rounded-full flex items-center justify-center">
                    <div className="w-36 h-36 bg-gradient-to-br from-emerald-500/30 to-emerald-600/30 rounded-full flex items-center justify-center">
                      <Lock className="w-16 h-16 text-emerald-400" />
                    </div>
                  </div>
                  <div className="absolute -top-4 -right-4 w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center animate-pulse">
                    <Shield className="w-8 h-8 text-red-400" />
                  </div>
                  <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center animate-pulse delay-500">
                    <Fingerprint className="w-6 h-6 text-blue-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            ¿Listo para comenzar?
          </h2>
          <p className="text-lg text-slate-400 mb-8">
            Únete a miles de usuarios que ya confían en OkChat para su comunicación empresarial.
          </p>
          <Button
            onClick={openSignup}
            className="h-14 px-8 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-medium text-lg"
            data-testid="cta-crear-cuenta-btn"
          >
            Crear cuenta gratis <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-slate-800/50 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white">OkChat</span>
            </div>

            <div className="flex items-center gap-4">
              <a href="https://x.com/okgomx" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 hover:bg-slate-700 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-5 h-5 text-slate-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a href="https://www.facebook.com/okgo.mexico" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 hover:bg-slate-700 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-5 h-5 text-slate-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="https://www.instagram.com/okgo.mx" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 hover:bg-slate-700 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-5 h-5 text-slate-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="https://www.linkedin.com/company/okgomx/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 hover:bg-slate-700 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-5 h-5 text-slate-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-slate-800/50 text-center text-sm text-slate-500">
            <p>© {new Date().getFullYear()} OkChat. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PoderesLanding;
